import { useState, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { toast } from 'react-toastify';
import { createManualReward, readStakeDbPoolTitles } from '@/api/stake-db';
import { CreateManualRewardRequest } from '@/types/stake-db';

export default function ManualRewardForm() {
  const [titles, setTitles] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    walletAddress: '',
    rewardAmount: '',
    rewardUsdt: '',
    txHash: '',
    rewardedAt: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTitles = async () => {
      try {
        const re = await readStakeDbPoolTitles();
        if (re.status === 200) {
          setTitles(re.data.titles || []);
        }
      } catch (error) {
        console.error('Failed to fetch pool titles:', error);
      }
    };
    fetchTitles();
  }, []);

  const handleChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error('Pool을 선택해주세요.');
      return;
    }
    if (!formData.walletAddress) {
      toast.error('지갑 주소를 입력해주세요.');
      return;
    }
    if (!formData.rewardAmount) {
      toast.error('리워드 수량을 입력해주세요.');
      return;
    }
    if (!formData.rewardUsdt) {
      toast.error('리워드 USDT를 입력해주세요.');
      return;
    }
    if (!formData.txHash) {
      toast.error('트랜잭션 해시를 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const data: CreateManualRewardRequest = {
        title: formData.title,
        walletAddress: formData.walletAddress,
        rewardAmount: formData.rewardAmount,
        rewardUsdt: formData.rewardUsdt,
        rewardCount: 1,
        txHash: formData.txHash,
        ...(formData.rewardedAt && { rewardedAt: formData.rewardedAt }),
      };

      const re = await createManualReward(data);

      if (re.status === 200 || re.status === 201) {
        toast.success(re.data.message || '수동 리워드 지급 성공');
        setFormData({
          title: '',
          walletAddress: '',
          rewardAmount: '',
          rewardUsdt: '',
          txHash: '',
          rewardedAt: '',
        });
      } else {
        toast.error(re.data?.message || '수동 리워드 지급 실패');
      }
    } catch (error) {
      console.error('Create manual reward error:', error);
      toast.error('수동 리워드 지급 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Typography variant="h6" gutterBottom>
            수동 리워드 지급
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            체인에서 리워드 지급 후 DB에 누락된 리워드를 수동으로 등록합니다.
          </Typography>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Pool (Title)</InputLabel>
            <Select
              value={formData.title}
              label="Pool (Title)"
              onChange={handleSelectChange('title')}
            >
              <MenuItem value="">선택 안 함</MenuItem>
              {titles.map((title) => (
                <MenuItem key={title} value={title}>
                  {title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Wallet Address</InputLabel>
            <OutlinedInput
              value={formData.walletAddress}
              label="Wallet Address"
              onChange={handleChange('walletAddress')}
              placeholder="0x..."
            />
          </FormControl>
        </Grid>

        <Grid xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Transaction Hash</InputLabel>
            <OutlinedInput
              value={formData.txHash}
              label="Transaction Hash"
              onChange={handleChange('txHash')}
              placeholder="0x..."
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Reward Amount (Token)</InputLabel>
            <OutlinedInput
              value={formData.rewardAmount}
              label="Reward Amount (Token)"
              onChange={handleChange('rewardAmount')}
              type="number"
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Reward USDT</InputLabel>
            <OutlinedInput
              value={formData.rewardUsdt}
              label="Reward USDT"
              onChange={handleChange('rewardUsdt')}
              type="number"
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel shrink>Rewarded At (Optional)</InputLabel>
            <OutlinedInput
              value={formData.rewardedAt}
              label="Rewarded At (Optional)"
              onChange={handleChange('rewardedAt')}
              placeholder="2024-11-12 18:10:37"
              notched
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Processing...' : '리워드 지급'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
